import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import apiService from '../services/api';

interface BannerData {
  id: string;
  message_fr: string;
  message_en: string;
  severity: 'info' | 'warning' | 'critical';
  starts_at: string | null;
  ends_at: string | null;
}

const DISMISSED_KEY = 'nubitech-banner-dismissed';
const POLL_MS = 5 * 60 * 1000; // refetch every 5 min

const styleFor = (severity: BannerData['severity']) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/15 text-red-300 border-red-500/40';
    case 'warning':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/40';
    default:
      return 'bg-blue-500/15 text-blue-300 border-blue-500/40';
  }
};

/**
 * Renders the most recent active banner at the top of the layout. Polls
 * /api/v1/banners/active every 5 minutes. Dismissal is per-banner-id and
 * stored in localStorage; if the admin updates the banner (new id) the
 * dismissal state resets automatically.
 */
const PlatformBanner: React.FC = () => {
  const { i18n } = (useTranslation as any)();
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(() =>
    localStorage.getItem(DISMISSED_KEY),
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const items = await apiService.fetchActiveBanners();
        if (cancelled) return;
        setBanner(items.length > 0 ? items[0] : null);
      } catch {
        // Endpoint optional — if it 404s or 500s, hide the banner silently.
        if (!cancelled) setBanner(null);
      }
    };
    void load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!banner || banner.id === dismissedId) return null;

  const message = i18n.language === 'en' ? banner.message_en : banner.message_fr;

  return (
    <div
      className={`flex items-start gap-3 border-b px-4 py-2 text-sm ${styleFor(banner.severity)}`}
      role="status"
    >
      <span className="flex-1 whitespace-pre-wrap">{message}</span>
      <button
        type="button"
        className="shrink-0 opacity-70 hover:opacity-100 text-xs px-2 py-0.5 rounded"
        onClick={() => {
          localStorage.setItem(DISMISSED_KEY, banner.id);
          setDismissedId(banner.id);
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default PlatformBanner;
